import { PracticeConfigurationEditor } from './PracticeConfigurationEditor';
import type { EventType } from './catalog.generated';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions';
import { useOrgTimezone } from '@/lib/timezone';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { flowsheetUrl } from './api';
import { gaDays } from './calculations';
import { checklistState, evaluateRules, type PracticeConfiguration } from './rules';
import type { ClinicalEvent } from './types';
import type { ProposedEntry } from './PrenatalEntry';

export function PracticeChecklist({
  prenatalFlags = [],
  events,
  edd,
  subjectId,
  onSave,
  disabled,
}: {
  prenatalFlags?: import('./prenatalRules').PrenatalFlag[];
  events: ClinicalEvent[];
  edd: string | null;
  subjectId: string;
  onSave: (entries: ProposedEntry[], time: string) => Promise<void>;
  disabled: boolean;
}) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const { permissions } = usePermissions();
  const { timeZone } = useOrgTimezone();
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ['flowsheet-configuration', orgId],
    enabled: !!orgId,
    queryFn: async () => (await clientApi.get<PracticeConfiguration>(`${flowsheetUrl(orgId!)}/configuration`)).data,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [test, setTest] = useState('');
  const [note, setNote] = useState('');
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const day = (key: string) => parts.find((p) => p.type === key)?.value;
  const days = gaDays(`${day('year')}-${day('month')}-${day('day')}`, edd);
  const config = query.data;
  async function record(event_type: EventType, payload: Record<string, unknown>) {
    setBusy(true);
    setError('');
    try {
      await onSave([{ subject_id: subjectId, event_type, payload }], new Date().toISOString());
      if (event_type === 'CONSENT.DECISION') setTest((current) => (current.trim() === payload.test ? '' : current));
      if (event_type === 'NOTE.PROGRESS') setNote((current) => (current.trim() === payload.text ? '' : current));
    } catch {
      setError('Entry was not saved. Keep this page open and retry without changing it.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Checklist and consent decisions</CardTitle>
        <CardDescription>
          Practice-reviewed checklist windows use the recorded EDD. A recorded decision is not a signed consent form.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {query.isError && (
          <Alert variant="destructive">
            <AlertDescription>Practice checklist could not be loaded.</AlertDescription>
          </Alert>
        )}
        {error && <p role="alert">{error}</p>}
        {config && (
          <>
            {[...evaluateRules(events, config), ...prenatalFlags].map((flag) => (
              <Alert
                key={`${flag.eventId}-${flag.ruleId}`}
                variant={flag.severity === 'critical' ? 'destructive' : 'default'}>
                <AlertDescription>
                  {flag.severity}: {flag.message} · rule {flag.ruleId}, version {flag.configurationVersion}
                </AlertDescription>
              </Alert>
            ))}
            {!config.checklist.length && <p>No practice checklist has been configured.</p>}
            {config.checklist.map((item) => (
              <div key={item.id} className="flex flex-wrap items-center gap-2">
                <span>{item.label}</span>
                {item.handout_url?.startsWith('https://') && (
                  <a
                    className="underline min-h-11 inline-flex items-center"
                    href={item.handout_url}
                    target="_blank"
                    rel="noopener noreferrer">
                    Patient handout
                  </a>
                )}
                {item.consent_test && (
                  <Button variant="outline" disabled={disabled || busy} onClick={() => setTest(item.consent_test!)}>
                    Document decision: {item.consent_test}
                  </Button>
                )}
                <Badge variant="outline">{checklistState(item, days, events)}</Badge>
                {permissions.includes('create_clinical_event') &&
                  ['COMPLETE', 'DEFERRED', 'NOT_APPLICABLE'].map((status) => (
                    <Button
                      key={status}
                      variant="outline"
                      size="sm"
                      className="min-h-11"
                      disabled={disabled || busy}
                      onClick={() =>
                        void record('CHECKLIST.ITEM', { item_id: item.id, template_version: config.version, status })
                      }>
                      {status === 'COMPLETE' ? 'Complete' : status === 'DEFERRED' ? 'Defer' : 'Not applicable'}
                    </Button>
                  ))}
              </div>
            ))}
          </>
        )}
        {permissions.includes('create_clinical_event') && (
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="consent-test">Test or procedure</FieldLabel>
              <Input id="consent-test" value={test} onChange={(e) => setTest(e.target.value)} />
            </Field>
            <div className="flex flex-wrap gap-2">
              {['CONSENTED', 'DECLINED', 'DEFERRED'].map((decision) => (
                <Button
                  key={decision}
                  variant="outline"
                  className="min-h-11"
                  disabled={disabled || busy || !test.trim()}
                  onClick={() => void record('CONSENT.DECISION', { test: test.trim(), decision })}>
                  {decision === 'CONSENTED'
                    ? 'Record consent'
                    : decision === 'DECLINED'
                      ? 'Record decline'
                      : 'Record deferral'}
                </Button>
              ))}
            </div>
            <Field>
              <FieldLabel htmlFor="prenatal-note">Visit note</FieldLabel>
              <Textarea id="prenatal-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <Button
              variant="outline"
              disabled={disabled || busy || !note.trim()}
              onClick={() => void record('NOTE.PROGRESS', { text: note.trim() })}>
              Record note
            </Button>
          </FieldGroup>
        )}
        {permissions.includes('configure_flowsheet') && config && (
          <PracticeConfigurationEditor
            key={orgId}
            configuration={config}
            publish={async (input) => {
              const result = (
                await clientApi.post<PracticeConfiguration>(`${flowsheetUrl(orgId!)}/configuration`, input)
              ).data;
              await cache.invalidateQueries({ queryKey: ['flowsheet-configuration', orgId] });
              return result;
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
